/**
 * SSE Event to Parts Adapter
 *
 * Converts Vercel AI SDK data stream events into UI message parts.
 * This keeps us compatible with the SDK's message structure while
 * using our manual SSE parser.
 */

import type { TextPart, ToolCallPart } from './types';

// AI SDK fullStream event types
interface SSETextDeltaEvent {
  type: 'text-delta';
  text: string;
}

interface SSEToolCallEvent {
  type: 'tool-call';
  toolCallId: string;
  toolName: string;
  input: unknown;
}

interface SSEToolResultEvent {
  type: 'tool-result';
  toolCallId: string;
  toolName: string;
  output: unknown;
}

// Additional event types we can safely ignore for UI purposes
interface SSEIgnoredEvent {
  type: 'start' | 'start-step' | 'finish-step' | 'finish' |
        'tool-input-start' | 'tool-input-delta' | 'tool-input-end' |
        'reasoning-start' | 'reasoning-delta' | 'reasoning-end' |
        'text-start' | 'text-end' | 'source' | 'file' | 'tool-error';
}

type SSEEvent =
  | SSETextDeltaEvent
  | SSEToolCallEvent
  | SSEToolResultEvent
  | SSEIgnoredEvent;

/**
 * Parses an SSE event into message parts compatible with AI SDK
 */
export function parseSSEEventToParts(
  evt: SSEEvent
): Array<TextPart | ToolCallPart> {
  switch (evt.type) {
    case 'text-delta':
      return [
        {
          type: 'text',
          text: evt.text || '',
        },
      ];

    case 'tool-call':
      return [
        {
          type: 'tool-call',
          toolCallId: evt.toolCallId,
          name: evt.toolName,
          input: evt.input,
          state: 'running', // Changed from 'input-available' to match UI rendering
        },
      ];

    case 'tool-result':
      return [
        {
          type: 'tool-call',
          toolCallId: evt.toolCallId,
          name: evt.toolName,
          input: undefined, // Already sent in tool-call
          result: evt.output,
          state: 'result',
        },
      ];

    default:
      return [];
  }
}

/**
 * Merges new parts into existing message parts
 * Handles both text accumulation and tool state updates
 */
export function mergeParts(
  existingParts: Array<TextPart | ToolCallPart>,
  newParts: Array<TextPart | ToolCallPart>
): Array<TextPart | ToolCallPart> {
  const merged = [...existingParts];

  for (const newPart of newParts) {
    if (newPart.type === 'text') {
      // Accumulate text into last text part or add new one
      const lastPart = merged[merged.length - 1];
      if (lastPart && lastPart.type === 'text') {
        lastPart.text += newPart.text;
      } else {
        merged.push(newPart);
      }
    } else {
      // Tool call part - update existing or add new
      const existingIndex = merged.findIndex(
        (p) =>
          p.type !== 'text' && p.toolCallId === newPart.toolCallId
      );

      if (existingIndex >= 0) {
        // Update existing tool call with new state/result
        merged[existingIndex] = {
          ...merged[existingIndex],
          ...newPart,
        };
      } else {
        // Add new tool call
        merged.push(newPart);
      }
    }
  }

  return merged;
}
